import "@theme/bcs-video-player";

const reduceMotionQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
const preferredScrollBehavior = (behavior = "smooth") =>
  reduceMotionQuery.matches ? "auto" : behavior;

const setupGalleryVideoPlayback = (stage, getActiveVideo) => {
  if (!(stage instanceof HTMLElement)) return;
  const videos = [...stage.querySelectorAll("video")];
  if (!videos.length) return;

  const controller = new AbortController();
  const states = new Map(
    videos.map((video) => [
      video,
      {
        automaticPause: false,
        manualPause: false,
        blocked: false,
        pending: null,
      },
    ]),
  );
  let destroyed = false;

  const isVisible = () => {
    if (destroyed || !stage.isConnected || document.hidden) return false;
    const bounds = stage.getBoundingClientRect();
    const width = Math.max(
      0,
      Math.min(bounds.right, window.innerWidth) - Math.max(bounds.left, 0),
    );
    const height = Math.max(
      0,
      Math.min(bounds.bottom, window.innerHeight) - Math.max(bounds.top, 0),
    );
    return (
      bounds.width * bounds.height > 0 &&
      (width * height) / (bounds.width * bounds.height) >= 0.35
    );
  };

  const pause = (video) => {
    const state = states.get(video);
    state.pending = null;
    if (video.paused) return;
    state.automaticPause = true;
    video.pause();
  };

  const sync = (userGesture = false) => {
    const activeVideo = isVisible() ? getActiveVideo() : null;
    for (const video of videos) {
      const state = states.get(video);
      if (video !== activeVideo) {
        pause(video);
        state.manualPause = false;
        state.blocked = false;
        continue;
      }
      if (userGesture) state.manualPause = false;
      if (
        !video.paused ||
        video.ended ||
        state.pending ||
        state.manualPause ||
        (state.blocked && !userGesture) ||
        (reduceMotionQuery.matches && !userGesture)
      )
        continue;

      const request = {};
      state.pending = request;
      // Keep play() in the click call stack so Safari can authorize sound.
      video
        .play()
        .then(() => {
          if (!isVisible() || video !== getActiveVideo()) pause(video);
        })
        .catch((error) => {
          if (state.pending === request && error.name === "NotAllowedError")
            state.blocked = true;
        })
        .finally(() => {
          if (state.pending === request) state.pending = null;
        });
    }
  };

  for (const video of videos) {
    video.playsInline = true;
    video.addEventListener(
      "play",
      () => {
        const state = states.get(video);
        state.blocked = false;
        state.manualPause = false;
        if (!isVisible() || video !== getActiveVideo()) pause(video);
      },
      { signal: controller.signal },
    );
    video.addEventListener(
      "pause",
      () => {
        const state = states.get(video);
        if (
          !state.automaticPause &&
          !state.pending &&
          !state.blocked &&
          !video.ended &&
          isVisible() &&
          video === getActiveVideo()
        )
          state.manualPause = true;
        state.automaticPause = false;
      },
      { signal: controller.signal },
    );
  }

  const gallery = stage.closest(".bcs-pdp-gallery");
  gallery?.addEventListener(
    "click",
    (event) => {
      if (
        event.isTrusted &&
        event.target instanceof Element &&
        event.target.closest(
          "[data-bcs-gallery-thumb], [data-bcs-gallery-prev], [data-bcs-gallery-next]",
        )
      )
        sync(true);
    },
    { signal: controller.signal },
  );
  gallery?.addEventListener(
    "touchend",
    (event) => {
      if (
        event.isTrusted &&
        event.target instanceof Element &&
        event.target.closest("[data-bcs-gallery-swiper]") &&
        !event.target.closest("bcs-video-player, video")
      )
        sync(true);
    },
    { signal: controller.signal },
  );
  document.addEventListener("visibilitychange", () => sync(), {
    signal: controller.signal,
  });
  window.addEventListener("pagehide", () => videos.forEach(pause), {
    signal: controller.signal,
  });
  window.addEventListener("pageshow", () => sync(), {
    signal: controller.signal,
  });
  const observer = new IntersectionObserver(() => sync(), {
    threshold: [0, 0.35],
  });
  observer.observe(stage);
  sync();

  return {
    sync,
    destroy() {
      destroyed = true;
      observer.disconnect();
      controller.abort();
      videos.forEach(pause);
    },
  };
};

const setupProductMarquee = (section) => {
  if (!(section instanceof HTMLElement) || section.bcsMarqueeCleanup) return;
  const slider = section.querySelector("[data-bcs-product-marquee-swiper]");
  if (!(slider instanceof HTMLElement)) return;

  let swiper;
  // Only pause on hover where a real mouse exists. On phones a tap fires
  // mouseenter with no mouseleave, which left the strip paused for good.
  const canHover = window.matchMedia("(hover: hover) and (pointer: fine)").matches;
  const keepRunning = () => {
    if (!swiper || swiper.destroyed || !swiper.autoplay) return;
    if (canHover && slider.matches(":hover")) return;
    if (swiper.autoplay.paused) swiper.autoplay.resume();
    if (!swiper.autoplay.running) swiper.autoplay.start();
  };
  const watchdog = window.setInterval(keepRunning, 4000);
  document.addEventListener("visibilitychange", keepRunning);
  const syncMotion = () => {
    if (
      reduceMotionQuery.matches ||
      window.Shopify?.designMode ||
      typeof window.Swiper !== "function"
    ) {
      swiper?.destroy(true, true);
      swiper = undefined;
      return;
    }
    if (swiper && !swiper.destroyed) return;

    swiper = new window.Swiper(slider, {
      slidesPerView: "auto",
      centeredSlides: false,
      loop: true,
      speed: Number(section.dataset.scrollDuration) * 1000,
      spaceBetween: 0,
      // Ignore taps and drags: a tap froze the strip for good while Swiper still
      // reported autoplay as running.
      allowTouchMove: false,
      freeMode: { enabled: true, momentum: false },
      autoplay: {
        delay: 0,
        disableOnInteraction: false,
        pauseOnMouseEnter: canHover,
      },
      a11y: { enabled: false },
    });
    // A tap or swipe stops free-mode autoplay even though Swiper still reports it as
    // "running", so always fully restart it shortly after the finger lifts.
    const restart = () => {
      if (!swiper || swiper.destroyed || !swiper.autoplay) return;
      swiper.autoplay.stop();
      swiper.autoplay.start();
    };
    swiper.on("touchEnd", () => window.setTimeout(restart, 600));
    swiper.on("click", () => window.setTimeout(restart, 600));
  };

  reduceMotionQuery.addEventListener("change", syncMotion);
  section.bcsMarqueeCleanup = () => {
    reduceMotionQuery.removeEventListener("change", syncMotion);
    window.clearInterval(watchdog);
    document.removeEventListener("visibilitychange", keepRunning);
    swiper?.destroy(true, true);
    delete section.bcsMarqueeCleanup;
  };
  syncMotion();
};

const setupBundleListing = (component) => {
  if (
    !(component instanceof HTMLElement) ||
    component.dataset.bcsBundleListingReady === "true"
  )
    return;
  const slider = component.querySelector("[data-bcs-bundle-listing-swiper]");
  const slides = slider
    ? [...slider.querySelectorAll(".bcs-bundle-listing__slide")]
    : [];
  if (
    !(slider instanceof HTMLElement) ||
    slides.length < 2 ||
    typeof window.Swiper !== "function"
  )
    return;

  component.dataset.bcsBundleListingReady = "true";
  component.bcsSwiper = new window.Swiper(slider, {
    slidesPerView: 1,
    slidesPerGroup: 1,
    spaceBetween: 16,
    speed: reduceMotionQuery.matches ? 0 : 420,
    autoHeight: true,
    a11y: {
      paginationBulletMessage: "Show gift {{index}}",
    },
    grabCursor: true,
    simulateTouch: true,
    allowTouchMove: true,
    touchStartPreventDefault: false,
    watchOverflow: true,
    observer: true,
    observeParents: true,
    pagination: {
      el: component.querySelector("[data-bcs-bundle-listing-pagination]"),
      clickable: true,
      bulletElement: "button",
    },
    navigation: {
      prevEl: component.querySelector("[data-bcs-bundle-listing-prev]"),
      nextEl: component.querySelector("[data-bcs-bundle-listing-next]"),
    },
  });

  component.addEventListener("shopify:block:select", (event) => {
    const slide = event.target.closest("[data-bcs-bundle-item]");
    const index = slides.indexOf(slide);
    if (index >= 0 && component.bcsSwiper && !component.bcsSwiper.destroyed) {
      component.bcsSwiper.slideTo(index, 0);
    }
  });
};

const formatSection = (section) => {
  if (
    !(section instanceof HTMLElement) ||
    section.dataset.bcsProductReady === "true"
  )
    return;
  section.dataset.bcsProductReady = "true";
  setupBundleListing(section.querySelector("[data-bcs-bundle-listing]"));

  const slides = [...section.querySelectorAll("[data-bcs-gallery-slide]")];
  const thumbs = [...section.querySelectorAll("[data-bcs-gallery-thumb]")];
  const galleryStage = section.querySelector(".bcs-pdp-gallery__stage");
  const galleryScrollbar = section.querySelector(
    "[data-bcs-gallery-scrollbar]",
  );
  const thumbsTrack = section.querySelector(".bcs-pdp-gallery__thumbs");
  const thumbPrev = section.querySelector("[data-bcs-thumb-prev]");
  const thumbNext = section.querySelector("[data-bcs-thumb-next]");
  const galleryCurrent = section.querySelector("[data-bcs-gallery-current]");
  const galleryPrev = section.querySelector("[data-bcs-gallery-prev]");
  const galleryNext = section.querySelector("[data-bcs-gallery-next]");
  const galleryModal = section.querySelector("[data-bcs-gallery-modal]");
  const modalTrack = section.querySelector("[data-bcs-gallery-modal-track]");
  const modalScrollbar = section.querySelector(
    "[data-bcs-gallery-modal-scrollbar]",
  );
  const modalSlides = [
    ...section.querySelectorAll("[data-bcs-gallery-modal-slide]"),
  ];
  const modalCurrent = section.querySelector(
    "[data-bcs-gallery-modal-current]",
  );
  const modalPrev = section.querySelector("[data-bcs-gallery-modal-prev]");
  const modalNext = section.querySelector("[data-bcs-gallery-modal-next]");
  const modalClose = section.querySelector("[data-bcs-gallery-modal-close]");
  let activeSlide = Math.max(
    0,
    slides.findIndex((slide) => slide.classList.contains("is-active")),
  );
  let modalActive = activeSlide;
  let gallerySwiper;
  let modalSwiper;
  let modalFrame;
  let galleryVideoPlayback;

  const pauseMedia = (root) =>
    root?.querySelectorAll("video").forEach((video) => video.pause());

  const updateThumbArrows = () => {
    if (!(thumbsTrack instanceof HTMLElement)) return;
    const maxScroll = thumbsTrack.scrollWidth - thumbsTrack.clientWidth;
    if (thumbPrev instanceof HTMLButtonElement)
      thumbPrev.disabled = thumbsTrack.scrollLeft <= 1;
    if (thumbNext instanceof HTMLButtonElement)
      thumbNext.disabled = thumbsTrack.scrollLeft >= maxScroll - 1;
  };

  [thumbPrev, thumbNext].forEach((arrow, direction) => {
    if (
      !(arrow instanceof HTMLButtonElement) ||
      !(thumbsTrack instanceof HTMLElement)
    )
      return;
    arrow.addEventListener("click", () => {
      const step = (thumbs[0]?.getBoundingClientRect().width || 0) + 10;
      thumbsTrack.scrollBy({
        left: direction === 0 ? -step : step,
        behavior: preferredScrollBehavior(),
      });
    });
  });
  thumbsTrack?.addEventListener("scroll", updateThumbArrows, { passive: true });
  if (
    thumbsTrack instanceof HTMLElement &&
    typeof ResizeObserver === "function"
  ) {
    const thumbResizeObserver = new ResizeObserver(updateThumbArrows);
    thumbResizeObserver.observe(thumbsTrack);
    section.bcsThumbsCleanup = () => thumbResizeObserver.disconnect();
  }
  updateThumbArrows();

  const updateGalleryState = (index) => {
    if (!slides.length) return;
    activeSlide = Math.max(0, Math.min(index, slides.length - 1));
    slides.forEach((slide, slideIndex) => {
      const active = slideIndex === activeSlide;
      slide.classList.toggle("is-active", active);
      slide.setAttribute("aria-hidden", String(!active));
      if (!active) pauseMedia(slide);
    });
    thumbs.forEach((thumb, thumbIndex) =>
      thumb.setAttribute("aria-current", String(thumbIndex === activeSlide)),
    );
    const activeThumb = thumbs[activeSlide];
    if (activeThumb && thumbsTrack instanceof HTMLElement) {
      thumbsTrack.scrollTo({
        left:
          activeThumb.offsetLeft -
          thumbs[0].offsetLeft -
          (thumbsTrack.clientWidth - activeThumb.clientWidth) / 2,
        behavior: preferredScrollBehavior(),
      });
    }
    if (galleryCurrent) galleryCurrent.textContent = String(activeSlide + 1);
    if (galleryPrev instanceof HTMLButtonElement)
      galleryPrev.disabled = activeSlide === 0;
    if (galleryNext instanceof HTMLButtonElement)
      galleryNext.disabled = activeSlide === slides.length - 1;
    galleryVideoPlayback?.sync();
  };

  const activateSlide = (index, behavior = "smooth") => {
    if (!slides.length) return;
    const nextIndex = Math.max(0, Math.min(index, slides.length - 1));
    updateGalleryState(nextIndex);
    if (gallerySwiper && !gallerySwiper.destroyed) {
      gallerySwiper.slideTo(
        nextIndex,
        behavior === "auto" || reduceMotionQuery.matches ? 0 : 420,
      );
    } else if (galleryStage instanceof HTMLElement) {
      galleryStage.scrollTo({
        left: slides[nextIndex].offsetLeft - slides[0].offsetLeft,
        behavior: preferredScrollBehavior(behavior),
      });
    }
  };

  if (
    galleryStage instanceof HTMLElement &&
    slides.length > 1 &&
    typeof window.Swiper === "function"
  ) {
    gallerySwiper = new window.Swiper(galleryStage, {
      slidesPerView: 1,
      slidesPerGroup: 1,
      spaceBetween: 0,
      speed: reduceMotionQuery.matches ? 0 : 420,
      grabCursor: true,
      simulateTouch: true,
      allowTouchMove: true,
      touchStartPreventDefault: false,
      watchOverflow: true,
      observer: true,
      observeParents: true,
      mousewheel: {
        forceToAxis: true,
        releaseOnEdges: true,
      },
      navigation: {
        prevEl: galleryPrev,
        nextEl: galleryNext,
      },
      scrollbar: {
        el: galleryScrollbar,
        draggable: true,
        hide: false,
      },
      on: {
        slideChange(swiper) {
          updateGalleryState(swiper.activeIndex);
        },
      },
    });
    galleryStage.bcsSwiper = gallerySwiper;
  }

  galleryVideoPlayback = setupGalleryVideoPlayback(galleryStage, () =>
    galleryModal?.open ? null : slides[activeSlide]?.querySelector("video"),
  );
  section.bcsGalleryVideoCleanup = () => galleryVideoPlayback?.destroy();

  const updateModalState = (index) => {
    modalActive = Math.max(0, Math.min(index, modalSlides.length - 1));
    if (modalCurrent) modalCurrent.textContent = String(modalActive + 1);
    if (modalPrev instanceof HTMLButtonElement)
      modalPrev.disabled = modalActive === 0;
    if (modalNext instanceof HTMLButtonElement)
      modalNext.disabled = modalActive === modalSlides.length - 1;
    modalSlides.forEach((slide, slideIndex) => {
      slide.setAttribute("aria-hidden", String(slideIndex !== modalActive));
      if (slideIndex !== modalActive) pauseMedia(slide);
    });
  };

  const initModalSwiper = () => {
    if (modalSwiper && !modalSwiper.destroyed) return modalSwiper;
    if (
      !(modalTrack instanceof HTMLElement) ||
      modalSlides.length < 2 ||
      typeof window.Swiper !== "function"
    )
      return null;

    modalSwiper = new window.Swiper(modalTrack, {
      initialSlide: modalActive,
      slidesPerView: 1,
      slidesPerGroup: 1,
      spaceBetween: 0,
      speed: reduceMotionQuery.matches ? 0 : 360,
      grabCursor: true,
      simulateTouch: true,
      allowTouchMove: true,
      touchStartPreventDefault: false,
      mousewheel: {
        forceToAxis: true,
        releaseOnEdges: true,
      },
      navigation: {
        prevEl: modalPrev,
        nextEl: modalNext,
      },
      scrollbar: {
        el: modalScrollbar,
        draggable: true,
        hide: false,
      },
      observer: true,
      observeParents: true,
      on: {
        init(swiper) {
          updateModalState(swiper.activeIndex);
        },
        slideChange(swiper) {
          updateModalState(swiper.activeIndex);
        },
      },
    });
    modalTrack.bcsSwiper = modalSwiper;
    return modalSwiper;
  };

  const goToModalSlide = (index, behavior = "smooth") => {
    if (!(modalTrack instanceof HTMLElement) || !modalSlides.length) return;
    const nextIndex = Math.max(0, Math.min(index, modalSlides.length - 1));
    updateModalState(nextIndex);
    if (modalSwiper && !modalSwiper.destroyed) {
      modalSwiper.slideTo(
        nextIndex,
        behavior === "auto" || reduceMotionQuery.matches ? 0 : 360,
      );
      return;
    }
    modalTrack.scrollTo({
      left: modalSlides[nextIndex].offsetLeft - modalSlides[0].offsetLeft,
      behavior: preferredScrollBehavior(behavior),
    });
  };

  const openGalleryModal = (index) => {
    if (!(galleryModal instanceof HTMLDialogElement)) return;
    modalActive = index;
    galleryModal.showModal();
    galleryVideoPlayback?.sync();
    document.documentElement.classList.add("bcs-gallery-open");
    window.requestAnimationFrame(() => {
      const swiper = initModalSwiper();
      swiper?.update();
      goToModalSlide(index, "auto");
    });
  };

  const closeGalleryModal = () => {
    if (!(galleryModal instanceof HTMLDialogElement) || !galleryModal.open)
      return;
    galleryModal.close();
  };

  thumbs.forEach((thumb, index) =>
    thumb.addEventListener("click", () => activateSlide(index)),
  );
  if (!gallerySwiper) {
    galleryPrev?.addEventListener("click", () =>
      activateSlide(activeSlide - 1),
    );
    galleryNext?.addEventListener("click", () =>
      activateSlide(activeSlide + 1),
    );
  }
  slides.forEach((slide, index) => {
    if (!slide.hasAttribute("data-bcs-gallery-zoom")) return;
    slide.addEventListener("click", () => {
      if (gallerySwiper && !gallerySwiper.allowClick) return;
      openGalleryModal(index);
    });
    slide.addEventListener("keydown", (event) => {
      if (event.key !== "Enter" && event.key !== " ") return;
      event.preventDefault();
      openGalleryModal(index);
    });
  });
  modalPrev?.addEventListener("click", () => {
    if (!modalSwiper) goToModalSlide(modalActive - 1);
  });
  modalNext?.addEventListener("click", () => {
    if (!modalSwiper) goToModalSlide(modalActive + 1);
  });
  modalClose?.addEventListener("click", closeGalleryModal);
  modalTrack?.addEventListener(
    "scroll",
    () => {
      window.cancelAnimationFrame(modalFrame);
      modalFrame = window.requestAnimationFrame(() => {
        if (modalSwiper) return;
        if (!(modalTrack instanceof HTMLElement)) return;
        const trackLeft = modalTrack.getBoundingClientRect().left;
        const nearest = modalSlides.reduce((current, slide, index) => {
          const distance = Math.abs(
            slide.getBoundingClientRect().left - trackLeft,
          );
          const currentDistance = Math.abs(
            modalSlides[current].getBoundingClientRect().left - trackLeft,
          );
          return distance < currentDistance ? index : current;
        }, 0);
        if (nearest !== modalActive) updateModalState(nearest);
      });
    },
    { passive: true },
  );
  galleryModal?.addEventListener("click", (event) => {
    if (event.target === galleryModal) closeGalleryModal();
  });
  galleryModal?.addEventListener("close", () => {
    document.documentElement.classList.remove("bcs-gallery-open");
    pauseMedia(galleryModal);
    activateSlide(modalActive, "auto");
  });
  galleryModal?.addEventListener("keydown", (event) => {
    if (event.key === "ArrowLeft") goToModalSlide(modalActive - 1);
    if (event.key === "ArrowRight") goToModalSlide(modalActive + 1);
  });

  const variantInput = section.querySelector("[data-bcs-variant-id]");
  const bundleInputs = [...section.querySelectorAll("[data-bcs-bundle-input]")];
  const purchaseInputs = [
    ...section.querySelectorAll("[data-bcs-purchase-input]"),
  ];
  const subscriptionInput = section.querySelector(
    '[data-bcs-purchase-input][value="subscription"]',
  );
  const oneTimeInput = section.querySelector(
    '[data-bcs-purchase-input][value="one-time"]',
  );
  const subscriptionOption = section.querySelector(
    "[data-bcs-subscription-option]",
  );
  const sellingPlanInput = section.querySelector(
    "[data-bcs-selling-plan-input]",
  );
  const price = section.querySelector("[data-bcs-main-price]");
  const comparePrice = section.querySelector("[data-bcs-compare-price]");
  const oneTimePrice = section.querySelector("[data-bcs-one-time-price]");
  const subscriptionPrice = section.querySelector(
    "[data-bcs-subscription-price]",
  );
  const subscriptionCompare = section.querySelector(
    "[data-bcs-subscription-compare]",
  );
  const addButton = section.querySelector("[data-bcs-add-button]");
  const addButtonPrice = section.querySelector("[data-bcs-add-price]");
  const addButtonLabel = section.querySelector("[data-bcs-add-label]");
  const quantityInput = section.querySelector("[data-bcs-quantity-input]");
  const quantityMinus = section.querySelector("[data-bcs-quantity-minus]");
  const quantityPlus = section.querySelector("[data-bcs-quantity-plus]");
  const planDropdowns = [
    ...section.querySelectorAll("[data-bcs-plan-dropdown]"),
  ];

  const activeBundle = () =>
    bundleInputs.find((input) => input.checked) || bundleInputs[0];
  const activePlanGroup = () => {
    const bundle = activeBundle();
    return bundle
      ? section.querySelector(
          `[data-bcs-plan-group][data-variant-id="${bundle.value}"]`,
        )
      : null;
  };
  const activePlanSelect = () =>
    activePlanGroup()?.querySelector("[data-bcs-plan-select]");

  const closePlanDropdowns = (except = null) => {
    planDropdowns.forEach((dropdown) => {
      if (dropdown === except) return;
      const trigger = dropdown.querySelector("[data-bcs-plan-trigger]");
      const menu = dropdown.querySelector("[data-bcs-plan-menu]");
      trigger?.setAttribute("aria-expanded", "false");
      if (menu instanceof HTMLElement) menu.hidden = true;
    });
  };

  const syncPlanDropdown = (select) => {
    if (!(select instanceof HTMLSelectElement)) return;
    const dropdown = select.closest("[data-bcs-plan-dropdown]");
    const selectedOption = select.selectedOptions[0];
    const label = dropdown?.querySelector("[data-bcs-plan-label]");
    if (label) label.textContent = selectedOption?.textContent?.trim() || "";
    dropdown?.querySelectorAll("[data-bcs-plan-option]").forEach((option) => {
      option.setAttribute(
        "aria-selected",
        String(option.dataset.value === select.value),
      );
    });
  };

  const syncQuantity = (nextValue) => {
    if (!(quantityInput instanceof HTMLInputElement)) return;
    const min = Number(quantityInput.min) || 1;
    const step = Number(quantityInput.step) || 1;
    const max = quantityInput.max
      ? Number(quantityInput.max)
      : Number.POSITIVE_INFINITY;
    const requestedValue = Math.max(min, Number(nextValue) || min);
    const requestedSteps = Math.round((requestedValue - min) / step);
    const maximumSteps = Number.isFinite(max)
      ? Math.max(0, Math.floor((max - min) / step))
      : Number.POSITIVE_INFINITY;
    const value =
      min + Math.min(Math.max(0, requestedSteps), maximumSteps) * step;
    quantityInput.value = String(value);
    if (quantityMinus instanceof HTMLButtonElement)
      quantityMinus.disabled = value <= min;
    if (quantityPlus instanceof HTMLButtonElement)
      quantityPlus.disabled = value + step > max;
  };

  const syncQuantityRules = (bundle) => {
    if (!(quantityInput instanceof HTMLInputElement) || !bundle) return;
    quantityInput.min = bundle.dataset.quantityMin || "1";
    quantityInput.step = bundle.dataset.quantityStep || "1";
    if (bundle.dataset.quantityMax)
      quantityInput.max = bundle.dataset.quantityMax;
    else quantityInput.removeAttribute("max");
    syncQuantity(quantityInput.value);
  };

  const setPrice = (current, compare = "") => {
    if (price) price.textContent = current || "";
    if (addButtonPrice)
      addButtonPrice.textContent = current ? ` ${current}` : "";
    if (comparePrice) {
      comparePrice.textContent = compare || "";
      comparePrice.toggleAttribute("hidden", !compare || compare === current);
    }
  };

  const syncPurchase = () => {
    const bundle = activeBundle();
    if (!bundle) return;
    const planSelect = activePlanSelect();
    const hasPlan = Boolean(planSelect?.options.length);

    if (subscriptionInput instanceof HTMLInputElement)
      subscriptionInput.disabled = !hasPlan;
    subscriptionOption?.classList.toggle("is-disabled", !hasPlan);
    if (
      !hasPlan &&
      subscriptionInput?.checked &&
      oneTimeInput instanceof HTMLInputElement
    )
      oneTimeInput.checked = true;

    const useSubscription = Boolean(subscriptionInput?.checked && hasPlan);
    const selectedPlan = planSelect?.selectedOptions[0];
    const currentPrice = useSubscription
      ? selectedPlan?.dataset.price
      : bundle.dataset.price;
    const currentCompare = useSubscription
      ? selectedPlan?.dataset.compare
      : bundle.dataset.compare;

    if (oneTimePrice) oneTimePrice.textContent = bundle.dataset.price || "";

    if (sellingPlanInput instanceof HTMLInputElement) {
      sellingPlanInput.disabled = !useSubscription;
      sellingPlanInput.value = useSubscription ? selectedPlan?.value || "" : "";
    }

    const url = new URL(window.location.href);
    if (useSubscription && selectedPlan?.value)
      url.searchParams.set("selling_plan", selectedPlan.value);
    else url.searchParams.delete("selling_plan");
    window.history.replaceState(window.history.state, "", url.toString());

    section.querySelectorAll("[data-bcs-plan-group]").forEach((group) => {
      const isCurrent = group === activePlanGroup() && useSubscription;
      group.toggleAttribute("hidden", !isCurrent);
      group.querySelectorAll("select").forEach((select) => {
        select.disabled = !isCurrent;
      });
    });

    if (subscriptionPrice)
      subscriptionPrice.textContent =
        selectedPlan?.dataset.price || bundle.dataset.price || "";
    if (subscriptionCompare) {
      const oneTimeComparePrice = bundle.dataset.price || "";
      const planPrice = selectedPlan?.dataset.price || "";
      subscriptionCompare.textContent = oneTimeComparePrice;
      subscriptionCompare.toggleAttribute(
        "hidden",
        !oneTimeComparePrice || oneTimeComparePrice === planPrice,
      );
    }
    syncPlanDropdown(planSelect);
    if (!useSubscription) closePlanDropdowns();
    setPrice(currentPrice || "", currentCompare || "");
  };

  const syncVariant = () => {
    const bundle = activeBundle();
    if (!bundle) return;
    if (variantInput instanceof HTMLInputElement)
      variantInput.value = bundle.value;
    syncQuantityRules(bundle);

    const isAvailable = bundle.dataset.available === "true";
    if (addButton instanceof HTMLButtonElement)
      addButton.disabled = !isAvailable;
    if (addButtonLabel)
      addButtonLabel.textContent = isAvailable
        ? addButtonLabel.dataset.availableText || "Add to cart"
        : "Sold out";

    const mediaId = bundle.dataset.mediaId;
    if (mediaId) {
      const mediaIndex = slides.findIndex(
        (slide) => slide.dataset.mediaId === mediaId,
      );
      if (mediaIndex >= 0) activateSlide(mediaIndex);
    }

    const url = new URL(window.location.href);
    url.searchParams.set("variant", bundle.value);
    window.history.replaceState(window.history.state, "", url.toString());
    syncPurchase();
  };

  bundleInputs.forEach((input) =>
    input.addEventListener("change", syncVariant),
  );
  purchaseInputs.forEach((input) =>
    input.addEventListener("change", syncPurchase),
  );
  section
    .querySelectorAll("[data-bcs-plan-select]")
    .forEach((select) => select.addEventListener("change", syncPurchase));
  planDropdowns.forEach((dropdown) => {
    const trigger = dropdown.querySelector("[data-bcs-plan-trigger]");
    const menu = dropdown.querySelector("[data-bcs-plan-menu]");

    trigger?.addEventListener("click", () => {
      const willOpen = trigger.getAttribute("aria-expanded") !== "true";
      closePlanDropdowns(willOpen ? dropdown : null);
      trigger.setAttribute("aria-expanded", String(willOpen));
      if (menu instanceof HTMLElement) menu.hidden = !willOpen;
    });

    dropdown.querySelectorAll("[data-bcs-plan-option]").forEach((option) => {
      option.addEventListener("click", () => {
        const select = dropdown.querySelector("[data-bcs-plan-select]");
        if (!(select instanceof HTMLSelectElement)) return;
        select.value = option.dataset.value || "";
        syncPurchase();
        closePlanDropdowns();
        trigger?.focus();
      });
    });
  });
  section.addEventListener("click", (event) => {
    if (
      event.target instanceof Element &&
      event.target.closest("[data-bcs-plan-dropdown]")
    )
      return;
    closePlanDropdowns();
  });
  section.addEventListener("keydown", (event) => {
    if (event.key !== "Escape") return;
    closePlanDropdowns();
  });
  quantityMinus?.addEventListener("click", () => {
    if (!(quantityInput instanceof HTMLInputElement)) return;
    syncQuantity(
      Number(quantityInput.value) - (Number(quantityInput.step) || 1),
    );
  });
  quantityPlus?.addEventListener("click", () => {
    if (!(quantityInput instanceof HTMLInputElement)) return;
    syncQuantity(
      Number(quantityInput.value) + (Number(quantityInput.step) || 1),
    );
  });
  quantityInput?.addEventListener("change", () =>
    syncQuantity(quantityInput.value),
  );

  activateSlide(activeSlide);
  syncVariant();
};

const setupEstimatedDelivery = (delivery) => {
  if (
    !(delivery instanceof HTMLElement) ||
    delivery.dataset.bcsDeliveryReady === "true"
  )
    return;
  delivery.dataset.bcsDeliveryReady = "true";

  const target = delivery.querySelector("[data-bcs-delivery-range]");
  if (!target) return;

  const minDays = Math.max(0, Number(delivery.dataset.minDays) || 2);
  const maxDays = Math.max(minDays, Number(delivery.dataset.maxDays) || 6);
  const start = new Date();
  const end = new Date();
  start.setHours(12, 0, 0, 0);
  end.setHours(12, 0, 0, 0);
  start.setDate(start.getDate() + minDays);
  end.setDate(end.getDate() + maxDays);

  const format = (date) =>
    new Intl.DateTimeFormat("en-US", { month: "short", day: "2-digit" }).format(
      date,
    );
  target.textContent = `${format(start)} - ${format(end)}`;
};

const setupResultsCarousel = (carousel) => {
  if (
    !(carousel instanceof HTMLElement) ||
    carousel.dataset.bcsResultsReady === "true"
  )
    return;
  carousel.dataset.bcsResultsReady = "true";

  const track = carousel.querySelector("[data-bcs-results-track]");
  const slides = [...carousel.querySelectorAll("[data-bcs-results-slide]")];
  const previous = carousel.querySelector("[data-bcs-results-prev]");
  const next = carousel.querySelector("[data-bcs-results-next]");
  const current = carousel.querySelector("[data-bcs-results-current]");
  let active = 0;
  let frame;

  if (!(track instanceof HTMLElement) || !slides.length) return;

  const syncControls = () => {
    if (current) current.textContent = String(active + 1);
    const maxScroll = Math.max(0, track.scrollWidth - track.clientWidth);
    if (previous instanceof HTMLButtonElement)
      previous.disabled = track.scrollLeft <= 1;
    if (next instanceof HTMLButtonElement)
      next.disabled = track.scrollLeft >= maxScroll - 1;
    carousel.classList.toggle("is-static", maxScroll <= 1);
  };

  const goTo = (index) => {
    active = Math.max(0, Math.min(index, slides.length - 1));
    const maxScroll = Math.max(0, track.scrollWidth - track.clientWidth);
    const target = Math.min(
      slides[active].offsetLeft - slides[0].offsetLeft,
      maxScroll,
    );
    track.scrollTo({ left: target, behavior: preferredScrollBehavior() });
    syncControls();
  };

  const syncFromScroll = () => {
    window.cancelAnimationFrame(frame);
    frame = window.requestAnimationFrame(() => {
      const trackLeft = track.getBoundingClientRect().left;
      active = slides.reduce((nearest, slide, index) => {
        const currentDistance = Math.abs(
          slide.getBoundingClientRect().left - trackLeft,
        );
        const nearestDistance = Math.abs(
          slides[nearest].getBoundingClientRect().left - trackLeft,
        );
        return currentDistance < nearestDistance ? index : nearest;
      }, 0);
      syncControls();
    });
  };

  previous?.addEventListener("click", () => goTo(active - 1));
  next?.addEventListener("click", () => goTo(active + 1));
  track.addEventListener("scroll", syncFromScroll, { passive: true });
  window.addEventListener("resize", syncFromScroll, { passive: true });
  carousel.bcsResultsCleanup = () => {
    window.cancelAnimationFrame(frame);
    window.removeEventListener("resize", syncFromScroll);
  };
  syncControls();
};

const setupRelatedProducts = (component) => {
  if (
    !(component instanceof HTMLElement) ||
    component.dataset.bcsRelatedReady === "true"
  )
    return;
  if (typeof window.Swiper !== "function") {
    window.addEventListener("load", () => setupRelatedProducts(component), {
      once: true,
    });
    return;
  }

  component.dataset.bcsRelatedReady = "true";
  const tabs = [...component.querySelectorAll("[data-bcs-related-tab]")];
  const panels = [...component.querySelectorAll("[data-bcs-related-panel]")];
  if (!tabs.length || !panels.length) return;

  const initPanelSwiper = (panel) => {
    const slider = panel.querySelector("[data-bcs-related-swiper]");
    if (!(slider instanceof HTMLElement)) return null;
    if (slider.swiper && !slider.swiper.destroyed) {
      slider.swiper.update();
      return slider.swiper;
    }

    const previous = panel.querySelector("[data-bcs-related-prev]");
    const next = panel.querySelector("[data-bcs-related-next]");
    const scrollbar = panel.querySelector("[data-bcs-related-scrollbar]");
    const current = panel.querySelector("[data-bcs-related-current]");

    const syncCurrent = (swiper) => {
      if (current) current.textContent = String(swiper.activeIndex + 1);
    };

    // Phones swipe with the browser's own scrolling (cssMode) so it feels smooth in
    // iPhone Safari, like the homepage scroller; desktop keeps the Swiper drag.
    const phoneNativeScroll = window.matchMedia("(max-width: 749px)").matches;
    return new window.Swiper(slider, {
      cssMode: phoneNativeScroll,
      slidesPerView: 1.55,
      slidesPerGroup: 1,
      spaceBetween: 16,
      speed: reduceMotionQuery.matches ? 0 : 420,
      grabCursor: true,
      simulateTouch: true,
      allowTouchMove: true,
      followFinger: true,
      threshold: 2,
      touchAngle: 55,
      shortSwipes: true,
      longSwipes: true,
      longSwipesMs: 300,
      longSwipesRatio: 0.12,
      resistanceRatio: 0.85,
      touchStartPreventDefault: false,
      watchOverflow: true,
      observer: true,
      observeParents: true,
      navigation: {
        prevEl: previous,
        nextEl: next,
      },
      scrollbar: {
        el: scrollbar,
        draggable: true,
      },
      breakpoints: {
        750: { slidesPerView: 3.1, spaceBetween: 16 },
        990: { slidesPerView: 3.3, spaceBetween: 18 },
        1200: { slidesPerView: 4, spaceBetween: 20 },
      },
      on: {
        init: syncCurrent,
        slideChange: syncCurrent,
      },
    });
  };

  const activateTab = (nextTab, focusTab = false) => {
    const panelId = nextTab.getAttribute("aria-controls");
    const nextPanel = panels.find((panel) => panel.id === panelId);
    if (!nextPanel) return;

    tabs.forEach((tab) => {
      const isActive = tab === nextTab;
      tab.setAttribute("aria-selected", String(isActive));
      tab.tabIndex = isActive ? 0 : -1;
    });
    panels.forEach((panel) => {
      panel.hidden = panel !== nextPanel;
    });

    window.requestAnimationFrame(() => initPanelSwiper(nextPanel)?.update());
    if (focusTab) nextTab.focus();
  };

  tabs.forEach((tab, index) => {
    tab.addEventListener("click", () => activateTab(tab));
    tab.addEventListener("keydown", (event) => {
      let nextIndex = index;
      if (event.key === "ArrowRight" || event.key === "ArrowDown")
        nextIndex = (index + 1) % tabs.length;
      else if (event.key === "ArrowLeft" || event.key === "ArrowUp")
        nextIndex = (index - 1 + tabs.length) % tabs.length;
      else if (event.key === "Home") nextIndex = 0;
      else if (event.key === "End") nextIndex = tabs.length - 1;
      else return;
      event.preventDefault();
      activateTab(tabs[nextIndex], true);
    });
  });

  activateTab(
    tabs.find((tab) => tab.getAttribute("aria-selected") === "true") || tabs[0],
  );
};

const setupProductRecommendations = (container) => {
  if (
    !(container instanceof HTMLElement) ||
    container.dataset.bcsRecommendationsReady === "true"
  )
    return;
  container.dataset.bcsRecommendationsReady = "true";

  if (container.dataset.recommendationsLoaded === "true") return;
  const url = container.dataset.url;
  if (!url) return;
  const abortController = new AbortController();
  let observer;

  container.bcsRecommendationsCleanup = () => {
    observer?.disconnect();
    abortController.abort();
  };

  const loadRecommendations = async () => {
    if (container.dataset.recommendationsLoading === "true") return;
    container.dataset.recommendationsLoading = "true";

    try {
      const response = await fetch(url, { signal: abortController.signal });
      if (!response.ok)
        throw new Error(
          `Recommendation request failed with ${response.status}`,
        );

      const html = new DOMParser().parseFromString(
        await response.text(),
        "text/html",
      );
      const incoming = [
        ...html.querySelectorAll("[data-bcs-product-recommendations]"),
      ].find(
        (element) => element.dataset.sectionId === container.dataset.sectionId,
      );

      if (!(incoming instanceof HTMLElement) || !incoming.innerHTML.trim()) {
        container
          .querySelector(".bcs-related-empty")
          ?.replaceChildren("Related products are unavailable.");
        return;
      }

      container
        .querySelectorAll("[data-bcs-related-swiper]")
        .forEach((slider) => {
          if (slider.swiper && !slider.swiper.destroyed)
            slider.swiper.destroy(true, true);
        });
      container.innerHTML = incoming.innerHTML;
      container.dataset.recommendationsLoaded = "true";
      setupProductPage(container);
    } catch (error) {
      if (error?.name === "AbortError") return;
      console.warn(
        "[bcs-product-recommendations] Could not load recommendations.",
        error,
      );
      container
        .querySelector(".bcs-related-empty")
        ?.replaceChildren("Related products are unavailable.");
    }
  };

  if (!("IntersectionObserver" in window)) {
    loadRecommendations();
    return;
  }

  observer = new IntersectionObserver(
    (entries) => {
      if (!entries[0]?.isIntersecting) return;
      observer.disconnect();
      loadRecommendations();
    },
    { rootMargin: "0px 0px 300px 0px" },
  );
  observer.observe(container);
};

const setupComparison = (comparison) => {
  if (
    !(comparison instanceof HTMLElement) ||
    comparison.dataset.bcsComparisonReady === "true"
  )
    return;
  comparison.dataset.bcsComparisonReady = "true";
  const input = comparison.querySelector('input[type="range"]');
  const sync = () =>
    comparison.style.setProperty("--compare", input?.value || "50");
  input?.addEventListener("input", sync);
  sync();
};

const setupProductAccordion = (accordion) => {
  if (
    !(accordion instanceof HTMLElement) ||
    accordion.dataset.bcsAccordionReady === "true"
  )
    return;
  accordion.dataset.bcsAccordionReady = "true";

  const items = [...accordion.querySelectorAll(".bcs-product-accordion__item")];
  const animations = new WeakMap();
  const motionOptions = {
    duration: 300,
    easing: "cubic-bezier(0.22, 1, 0.36, 1)",
    fill: "forwards",
  };

  const stopItemAnimation = (item) => {
    const animation = animations.get(item);
    if (!animation) return;
    if (typeof animation.commitStyles === "function") animation.commitStyles();
    animation.cancel();
    animations.delete(item);
  };

  const resetContent = (item, content) => {
    const animation = animations.get(item);
    if (animation) animation.cancel();
    delete item.dataset.bcsAccordionState;
    content.removeAttribute("style");
    animations.delete(item);
  };

  const closeItem = (item, animate = true) => {
    if (!(item instanceof HTMLDetailsElement) || !item.open) return;
    const content = item.querySelector("[data-bcs-accordion-content]");
    if (!(content instanceof HTMLElement)) {
      item.open = false;
      return;
    }

    stopItemAnimation(item);
    if (!animate || reduceMotionQuery.matches) {
      item.open = false;
      resetContent(item, content);
      return;
    }

    const startHeight = content.getBoundingClientRect().height;
    const startPadding = window.getComputedStyle(content).paddingBottom;
    item.dataset.bcsAccordionState = "closing";
    const animation = content.animate(
      [
        { height: `${startHeight}px`, paddingBottom: startPadding, opacity: 1 },
        { height: "0px", paddingBottom: "0px", opacity: 0 },
      ],
      motionOptions,
    );
    animations.set(item, animation);
    animation.addEventListener(
      "finish",
      () => {
        if (item.dataset.bcsAccordionState !== "closing") return;
        item.open = false;
        resetContent(item, content);
      },
      { once: true },
    );
  };

  const openItem = (item) => {
    if (!(item instanceof HTMLDetailsElement)) return;
    const content = item.querySelector("[data-bcs-accordion-content]");
    if (!(content instanceof HTMLElement)) return;

    items.forEach((otherItem) => {
      if (otherItem !== item) closeItem(otherItem, !reduceMotionQuery.matches);
    });

    stopItemAnimation(item);
    item.open = true;
    if (reduceMotionQuery.matches) {
      resetContent(item, content);
      return;
    }

    content.removeAttribute("style");
    const endHeight = content.scrollHeight;
    const endPadding = window.getComputedStyle(content).paddingBottom;
    item.dataset.bcsAccordionState = "opening";
    const animation = content.animate(
      [
        { height: "0px", paddingBottom: "0px", opacity: 0 },
        { height: `${endHeight}px`, paddingBottom: endPadding, opacity: 1 },
      ],
      motionOptions,
    );
    animations.set(item, animation);
    animation.addEventListener(
      "finish",
      () => {
        if (item.dataset.bcsAccordionState !== "opening") return;
        resetContent(item, content);
      },
      { once: true },
    );
  };

  const initiallyOpen = items.filter(
    (item) => item instanceof HTMLDetailsElement && item.open,
  );
  initiallyOpen.slice(1).forEach((item) => closeItem(item, false));

  items.forEach((item) => {
    const summary = item.querySelector("summary");
    summary?.addEventListener("click", (event) => {
      event.preventDefault();
      if (item.open && item.dataset.bcsAccordionState !== "closing")
        closeItem(item);
      else openItem(item);
    });
  });
};

const setupIngredientTabs = (component) => {
  if (
    !(component instanceof HTMLElement) ||
    component.dataset.bcsIngredientTabsReady === "true"
  )
    return;

  const tabs = [...component.querySelectorAll("[data-bcs-ingredient-tab]")];
  const panels = [...component.querySelectorAll("[data-bcs-ingredient-panel]")];
  if (tabs.length < 2 || tabs.length !== panels.length) return;

  component.dataset.bcsIngredientTabsReady = "true";

  const selectTab = (nextIndex, moveFocus = false) => {
    tabs.forEach((tab, index) => {
      const isSelected = index === nextIndex;
      tab.setAttribute("aria-selected", String(isSelected));
      tab.setAttribute("tabindex", isSelected ? "0" : "-1");
      panels[index].hidden = !isSelected;
    });

    if (moveFocus) tabs[nextIndex].focus();
  };

  tabs.forEach((tab, index) => {
    tab.addEventListener("click", () => selectTab(index));
    tab.addEventListener("keydown", (event) => {
      let nextIndex = index;
      if (event.key === "ArrowRight") nextIndex = (index + 1) % tabs.length;
      else if (event.key === "ArrowLeft")
        nextIndex = (index - 1 + tabs.length) % tabs.length;
      else if (event.key === "Home") nextIndex = 0;
      else if (event.key === "End") nextIndex = tabs.length - 1;
      else return;

      event.preventDefault();
      selectTab(nextIndex, true);
    });
  });
};

const setupRoutineTabs = (component) => {
  if (
    !(component instanceof HTMLElement) ||
    component.dataset.bcsRoutineReady === "true"
  )
    return;

  const tabs = [...component.querySelectorAll("[data-bcs-routine-tab]")];
  const panels = [...component.querySelectorAll("[data-bcs-routine-panel]")];
  if (tabs.length === 0 || tabs.length !== panels.length) return;

  component.dataset.bcsRoutineReady = "true";

  const selectTab = (nextIndex, moveFocus = false) => {
    tabs.forEach((tab, index) => {
      const isSelected = index === nextIndex;
      tab.setAttribute("aria-selected", String(isSelected));
      tab.setAttribute("tabindex", isSelected ? "0" : "-1");
      panels[index].hidden = !isSelected;
    });

    const activeTrack = panels[nextIndex]?.querySelector(
      ".bcs-routine-step-grid",
    );
    if (activeTrack instanceof HTMLElement)
      activeTrack.scrollTo({ left: 0, behavior: "auto" });
    if (moveFocus) tabs[nextIndex].focus();
  };

  tabs.forEach((tab, index) => {
    tab.addEventListener("click", () => selectTab(index));
    tab.addEventListener("keydown", (event) => {
      let nextIndex = index;
      if (event.key === "ArrowRight") nextIndex = (index + 1) % tabs.length;
      else if (event.key === "ArrowLeft")
        nextIndex = (index - 1 + tabs.length) % tabs.length;
      else if (event.key === "Home") nextIndex = 0;
      else if (event.key === "End") nextIndex = tabs.length - 1;
      else return;

      event.preventDefault();
      selectTab(nextIndex, true);
    });
  });

  component.addEventListener("shopify:block:select", (event) => {
    const panel = event.target.closest("[data-bcs-routine-panel]");
    const panelIndex = panels.indexOf(panel);
    if (panelIndex >= 0) selectTab(panelIndex);
  });
};

const setupProductFaq = (faq) => {
  if (!(faq instanceof HTMLElement) || faq.dataset.bcsFaqReady === "true")
    return;
  faq.dataset.bcsFaqReady = "true";

  const tabs = [...faq.querySelectorAll("[data-bcs-faq-tab]")];
  const list = faq.querySelector("[data-bcs-faq-list]");
  const items = [...faq.querySelectorAll("[data-bcs-faq-category]")];
  if (!(list instanceof HTMLElement) || tabs.length === 0) return;

  const selectCategory = (category, animate = true) => {
    const oldHeight = list.getBoundingClientRect().height;

    tabs.forEach((tab) => {
      const isActive = tab.dataset.bcsFaqTab === category;
      tab.setAttribute("aria-selected", String(isActive));
      tab.setAttribute("tabindex", isActive ? "0" : "-1");
    });

    items.forEach((item) => {
      const isVisible = item.dataset.bcsFaqCategory === category;
      if (!isVisible && item instanceof HTMLDetailsElement) {
        item.open = false;
        delete item.dataset.bcsAccordionState;
        item
          .querySelector("[data-bcs-accordion-content]")
          ?.removeAttribute("style");
      }
      item.hidden = !isVisible;
    });

    if (!animate || reduceMotionQuery.matches) return;
    const newHeight = list.scrollHeight;
    list.animate(
      [
        {
          height: `${oldHeight}px`,
          opacity: 0.35,
          transform: "translateY(4px)",
        },
        { height: `${newHeight}px`, opacity: 1, transform: "translateY(0)" },
      ],
      {
        duration: 280,
        easing: "cubic-bezier(0.22, 1, 0.36, 1)",
      },
    );
  };

  tabs.forEach((tab, index) => {
    tab.addEventListener("click", () => selectCategory(tab.dataset.bcsFaqTab));
    tab.addEventListener("keydown", (event) => {
      let nextIndex = index;
      if (event.key === "ArrowRight") nextIndex = (index + 1) % tabs.length;
      else if (event.key === "ArrowLeft")
        nextIndex = (index - 1 + tabs.length) % tabs.length;
      else if (event.key === "Home") nextIndex = 0;
      else if (event.key === "End") nextIndex = tabs.length - 1;
      else return;
      event.preventDefault();
      tabs[nextIndex].focus();
      tabs[nextIndex].click();
    });
  });
};

const setupProductPage = (root = document) => {
  root
    .querySelectorAll("[data-bcs-product-marquee]")
    .forEach(setupProductMarquee);
  root
    .querySelectorAll("[data-bcs-product-recommendations]")
    .forEach(setupProductRecommendations);
  root.querySelectorAll("[data-bcs-product-hero]").forEach(formatSection);
  root.querySelectorAll("[data-bcs-comparison]").forEach(setupComparison);
  root
    .querySelectorAll("[data-bcs-estimated-delivery]")
    .forEach(setupEstimatedDelivery);
  root
    .querySelectorAll("[data-bcs-results-carousel]")
    .forEach(setupResultsCarousel);
  root
    .querySelectorAll("[data-bcs-related-products]")
    .forEach(setupRelatedProducts);
  root
    .querySelectorAll("[data-bcs-routine-component]")
    .forEach(setupRoutineTabs);
  root
    .querySelectorAll("[data-bcs-ingredient-tabs]")
    .forEach(setupIngredientTabs);
  root
    .querySelectorAll("[data-bcs-product-accordion]")
    .forEach(setupProductAccordion);
  root.querySelectorAll("[data-bcs-product-faq]").forEach(setupProductFaq);
};

setupProductPage();

document.addEventListener("shopify:section:load", (event) =>
  setupProductPage(event.target),
);
document.addEventListener("shopify:section:unload", (event) => {
  event.target
    .querySelectorAll("[data-bcs-product-marquee]")
    .forEach((section) => section.bcsMarqueeCleanup?.());
  event.target
    .querySelectorAll("[data-bcs-product-hero]")
    .forEach((section) => {
      section.bcsThumbsCleanup?.();
      section.bcsGalleryVideoCleanup?.();
    });
  event.target
    .querySelectorAll("[data-bcs-product-recommendations]")
    .forEach((container) => container.bcsRecommendationsCleanup?.());
  event.target
    .querySelectorAll("[data-bcs-results-carousel]")
    .forEach((carousel) => {
      carousel.bcsResultsCleanup?.();
    });
  event.target
    .querySelectorAll("[data-bcs-related-swiper]")
    .forEach((slider) => {
      if (slider.swiper && !slider.swiper.destroyed)
        slider.swiper.destroy(true, true);
    });
  event.target
    .querySelectorAll("[data-bcs-bundle-listing]")
    .forEach((component) => {
      component.bcsSwiper?.destroy(true, true);
    });
  event.target
    .querySelectorAll("[data-bcs-gallery-swiper]")
    .forEach((slider) => {
      slider.bcsSwiper?.destroy(true, true);
    });
  event.target
    .querySelectorAll("[data-bcs-gallery-modal-track]")
    .forEach((slider) => {
      slider.bcsSwiper?.destroy(true, true);
    });
});
